import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { cn } from '@aia/ui-components';

import { DashboardLayout } from './components/layout/DashboardLayout';
import { ProtectedRoute } from './lib/auth';

const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const SsoLandingPage = lazy(() => import('./pages/SsoLandingPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const AssessmentsPage = lazy(() => import('./pages/AssessmentsPage'));
const AssessmentsUploadPage = lazy(() => import('./pages/AssessmentsUploadPage'));
const CrosswalkPage = lazy(() => import('./pages/CrosswalkPage'));
const DashboardViewsPage = lazy(() => import('./pages/DashboardViewsPage'));
const DashboardViewPage = lazy(() => import('./pages/DashboardViewPage'));
const CreateDashboardViewPage = lazy(() => import('./pages/CreateDashboardViewPage'));

function PageFallback() {
  return (
    <div className={cn('flex min-h-screen items-center justify-center')} role="status">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      <span className="sr-only">Loading page...</span>
    </div>
  );
}

function App() {
  return (
    <>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/sso" element={<SsoLandingPage />} />

          {/* Protected dashboard-layout routes */}
          <Route
            element={
              <ProtectedRoute allowedRoles={['OrgAdmin', 'UnitAdmin', 'Instructor']}>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/assessments" element={<AssessmentsPage />} />
            <Route
              path="/assessments/upload"
              element={
                <ProtectedRoute allowedRoles={['OrgAdmin', 'UnitAdmin']}>
                  <AssessmentsUploadPage />
                </ProtectedRoute>
              }
            />
            <Route path="/crosswalk" element={<CrosswalkPage />} />
            <Route path="/dashboard-views" element={<DashboardViewsPage />} />
            <Route path="/dashboard-views/new" element={<CreateDashboardViewPage />} />
            <Route path="/dashboard-views/:viewId" element={<DashboardViewPage />} />
            <Route path="/dashboard-views/:viewId/edit" element={<CreateDashboardViewPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
